import { useState, useEffect, useCallback } from 'react';
import { getApiUrl } from '../config/api';

const useClients = (token) => {
  const [clients, setClients] = useState([]);
  const [originalClients, setOriginalClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('clientId');
  const [sortDirection, setSortDirection] = useState('asc');
  const [tableLocked, setTableLocked] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [editedData, setEditedData] = useState({});

  // Fetch clients from API
  const fetchClients = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(getApiUrl('/api/clients'), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setClients(data || []);
      setOriginalClients(JSON.parse(JSON.stringify(data || []))); // Deep copy
      setEditedData({}); // Clear any previous edits
      setHasChanges(false);
    } catch (err) {
      console.error('Error fetching clients:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);


  // Update a single client
  const updateClient = useCallback((clientId, field, value) => {
    if (tableLocked) return; // Prevent updates when table is locked
    
    console.log('updateClient called:', { clientId, field, value });
    
    // Update the clients array for immediate UI feedback
    setClients(prevClients => {
      const updated = prevClients.map(client => {
        if (client.clientId === clientId) {
          return { ...client, [field]: value };
        }
        return client;
      });
      return updated;
    });
    
    // Update editedData to track changes, but check if the final state matches original
    setEditedData(prev => {
      const newEditedData = {
        ...prev,
        [clientId]: {
          ...prev[clientId],
          [field]: value
        }
      };
      
      // Check if this client now matches its original state
      const originalClient = originalClients.find(c => c.clientId === clientId);
      if (originalClient) {
        const currentClient = clients.find(c => c.clientId === clientId);
        const updatedClient = { ...currentClient, [field]: value };
        
        // Deep comparison to see if client matches original
        const isSame = JSON.stringify(updatedClient) === JSON.stringify(originalClient);
        if (isSame) {
          // Remove from editedData if it matches original
          const { [clientId]: removed, ...rest } = newEditedData;
          return rest;
        }
      }
      
      return newEditedData;
    });
  }, [tableLocked, originalClients, clients]);

  // Revert a single client field without tracking as a change
  const revertClient = useCallback((clientId, field, originalValue) => {
    if (tableLocked) return; // Prevent revert when table is locked
    
    // Update the clients array directly without adding to editedData
    setClients(prevClients => {
      return prevClients.map(client => {
        if (client.clientId === clientId) {
          return { ...client, [field]: originalValue };
        }
        return client;
      });
    });
    
    // Also remove this field from editedData if it exists
    setEditedData(prev => {
      const clientData = prev[clientId];
      if (clientData) {
        const { [field]: removed, ...restFields } = clientData;
        
        // If no fields left for this client, remove the entire client entry
        if (Object.keys(restFields).length === 0) {
          const { [clientId]: removedClient, ...restClients } = prev;
          return restClients;
        } else {
          return { ...prev, [clientId]: restFields };
        }
      }
      return prev;
    });
  }, [tableLocked]);

  // Handle sorting
  const handleSort = useCallback((field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  }, [sortField, sortDirection]);

  // Get sort indicator
  const getSortIndicator = useCallback((field) => {
    if (sortField !== field) return '';
    return sortDirection === 'asc' ? '↑' : '↓';
  }, [sortField, sortDirection]);

  // Filter and sort clients
  const filteredClients = clients
    .filter(client => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      return (
        (client.clientId && client.clientId.toLowerCase().includes(searchLower)) ||
        (client.name && client.name.toLowerCase().includes(searchLower)) ||
        (client.email && client.email.toLowerCase().includes(searchLower)) ||
        (client.company && client.company.toLowerCase().includes(searchLower))
      );
    })
    .sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';

      // Convert to strings for comparison
      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();

      if (sortDirection === 'asc') {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });


  // Format currency
  const formatPrice = useCallback((price) => {
    if (typeof price !== 'number') return 'N/A';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  }, []);

  // Check if client has low stock (for consistency with products interface)
  const isLowStock = useCallback((client) => {
    // Clients don't have stock, but we can define other criteria if needed
    return false;
  }, []);

  // Save changes
  const saveChanges = useCallback(async () => {
    if (!token || !hasChanges || Object.keys(editedData).length === 0) return;

    setTableLocked(true);
    setUpdating(true);
    setError(null);

    try {
      // Convert editedData to array of updated clients with clientId included
      const updatedClients = Object.entries(editedData).map(([clientId, changes]) => ({
        clientId,
        ...changes
      }));
      
      const response = await fetch(getApiUrl('/api/clients'), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ clients: updatedClients }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      // Update original clients with the saved changes
      setOriginalClients(prev => {
        const updated = [...prev];
        Object.values(editedData).forEach(updatedClient => {
          const index = updated.findIndex(c => c.clientId === updatedClient.clientId);
          if (index !== -1) {
            updated[index] = { ...updatedClient };
          }
        });
        return updated;
      });

      setEditedData({}); // Clear edited data
      setHasChanges(false);
      
      // Refresh clients from server to ensure consistency
      await fetchClients();
      
      return true;
    } catch (err) {
      console.error('Error saving changes:', err);
      setError('Error saving changes: ' + err.message);
      return false;
    } finally {
      setUpdating(false);
      setTableLocked(false);
    }
  }, [token, hasChanges, editedData, fetchClients]);

  // Undo changes
  const undoChanges = useCallback(() => {
    if (tableLocked) return; // Prevent undo when table is locked
    setClients(JSON.parse(JSON.stringify(originalClients))); // Deep copy
    setEditedData({}); // Clear edited data
    setHasChanges(false);
  }, [originalClients, tableLocked]);

  // Check for changes based on editedData
  useEffect(() => {
    const hasChanges = Object.keys(editedData).length > 0;
    setHasChanges(hasChanges);
  }, [editedData]);

  // Load clients on mount and when token changes
  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  return {
    clients: filteredClients,
    loading,
    error,
    updating,
    searchTerm,
    setSearchTerm,
    updateClient,
    revertClient,
    handleSort,
    getSortIndicator,
    formatPrice,
    isLowStock,
    tableLocked,
    setTableLocked,
    refreshClients: fetchClients,
    hasChanges,
    editedData,
    saveChanges,
    undoChanges
  };
};

export default useClients;
